import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import styled from 'styled-components';
import { FaGripVertical, FaTrophy, FaBuilding, FaMapMarker, FaTag, FaExclamationTriangle, FaPlus, FaEye, FaEyeSlash } from 'react-icons/fa';
import { PLACE_DU_DESIGN_OPTIONS, PROFIL_CREATEUR_OPTIONS } from '../sdeConstants';
import { makeUrlsAndEmailsLinks } from '../../../Utils/Format';

const theme = {
  colors: {
    primary: '#2563EB',
    primaryLight: '#DBEAFE',
    secondary: '#7C3AED',
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    gray: {
      50: '#F9FAFB',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      400: '#9CA3AF',
      500: '#6B7280',
      600: '#4B5563',
      700: '#374151',
      800: '#1F2937',
    }
  },
  spacing: {
    xs: '0.5rem',
    sm: '1rem',
    md: '1.5rem',
    lg: '2rem',
  },
  borderRadius: {
    sm: '0.375rem',
    md: '0.5rem',
    lg: '0.75rem',
  },
  transition: 'all 0.2s ease-in-out',
};

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${theme.spacing.lg};
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing.lg};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const SectionHeader = styled.div`
  padding: ${theme.spacing.md};
  background: ${theme.colors.gray[50]};
  border-bottom: 1px solid ${theme.colors.gray[200]};
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: ${theme.colors.gray[800]};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const Badge = styled.span`
  background: ${props => props.variant === 'primary' ? theme.colors.primary : theme.colors.gray[500]};
  color: white;
  padding: 2px 8px;
  border-radius: ${theme.borderRadius.sm};
  font-size: 0.875rem;
  font-weight: 500;
`;

const CompanyList = styled.div`
  padding: ${theme.spacing.md};
  max-height: 600px;
  overflow-y: auto;
`;

const CompanyCard = styled.div`
  background: white;
  border: 2px solid ${theme.colors.gray[200]};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.sm};
  cursor: pointer;
  transition: ${theme.transition};
  
  &:hover {
    border-color: ${theme.colors.primary};
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }
  
  ${props => props.selected && `
    border-color: ${theme.colors.primary};
    background: ${theme.colors.primaryLight};
  `}
`;

const CompanyName = styled.h4`
  margin: 0 0 ${theme.spacing.xs} 0;
  font-size: 1rem;
  font-weight: 600;
  color: ${theme.colors.gray[800]};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
`;

const CompanyDetails = styled.div`
  font-size: 0.875rem;
  color: ${theme.colors.gray[600]};
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.sm};
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  
  svg {
    color: ${theme.colors.gray[400]};
  }
`;

const CompanyDescription = styled.p`
  font-size: 0.875rem;
  color: ${theme.colors.gray[600]};
  margin: 0;
  line-height: 1.5;
`;

const NewTag = styled.span`
  background: ${theme.colors.success};
  color: white;
  padding: 2px 6px;
  border-radius: ${theme.borderRadius.sm};
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
`;

const DroppableArea = styled.div`
  min-height: 400px;
  padding: ${theme.spacing.md};
  background: ${props => props.isDraggingOver ? theme.colors.gray[50] : 'white'};
  transition: background-color 0.2s ease;
`;

const DraggableCard = styled.div`
  background: white;
  border: 2px solid ${props => props.isDragging ? theme.colors.primary : theme.colors.gray[200]};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.sm};
  box-shadow: ${props => props.isDragging ? '0 10px 15px -3px rgba(0, 0, 0, 0.1)' : '0 1px 3px 0 rgba(0, 0, 0, 0.1)'};
  transition: ${theme.transition};
  
  &:hover {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
`;

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  color: ${theme.colors.gray[400]};
  cursor: grab;
  
  &:active {
    cursor: grabbing;
  }
`;

const PriorityNumber = styled.div`
  width: 32px;
  height: 32px;
  background: ${props => 
    props.priority <= 3 ? theme.colors.success :
    props.priority <= 7 ? theme.colors.warning :
    theme.colors.gray[400]};
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.875rem;
  margin-right: ${theme.spacing.sm};
`;

const CardContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing.xl};
  color: ${theme.colors.gray[500]};
  
  svg {
    font-size: 3rem;
    margin-bottom: ${theme.spacing.sm};
    opacity: 0.3;
  }
  
  p {
    margin: 0;
    font-size: 0.875rem;
  }
`;

const WarningMessage = styled.div`
  background: ${theme.colors.warning}10;
  border: 1px solid ${theme.colors.warning}30;
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.sm};
  margin: ${theme.spacing.md};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-size: 0.875rem;
  color: ${theme.colors.warning}DD;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${theme.spacing.sm};
  border: 1px solid ${theme.colors.gray[300]};
  border-radius: ${theme.borderRadius.md};
  font-size: 0.875rem;
  margin-bottom: ${theme.spacing.md};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primary}20;
  }
`;

const ExpandToggle = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${theme.colors.primary};
  font-size: 0.8rem;
  font-weight: 500;
  padding: 0;
  margin-top: 0.4rem;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  &:hover { text-decoration: underline; }
`;

const ExpandedPanel = styled.div`
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid ${theme.colors.gray[200]};
`;

const DetailBlock = styled.div`
  margin-bottom: 0.75rem;
  .detail-label {
    font-size: 0.8rem;
    font-weight: 600;
    color: ${theme.colors.gray[700]};
    margin-bottom: 0.25rem;
  }
  .detail-value {
    font-size: 0.85rem;
    color: ${theme.colors.gray[600]};
    line-height: 1.5;
    white-space: pre-wrap;
    a {
      color: ${theme.colors.primary};
      text-decoration: underline;
      &:hover { color: #1D4ED8; }
    }
  }
`;

const AnnonceTagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  margin-top: 0.25rem;
`;

const AnnonceTag = styled.span`
  display: inline-block;
  padding: 2px 8px;
  background: ${theme.colors.primaryLight};
  color: ${theme.colors.primary};
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
`;

const ProjectItem = styled.div`
  background: ${theme.colors.gray[50]};
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
  margin-bottom: 0.4rem;
  .proj-title { font-weight: 600; color: ${theme.colors.gray[700]}; font-size: 0.85rem; }
  .proj-detail { color: ${theme.colors.gray[500]}; font-size: 0.8rem; margin-top: 2px; }
  a {
    color: ${theme.colors.primary};
    text-decoration: underline;
    &:hover { color: #1D4ED8; }
  }
`;

const AddBtn = styled.button`
  margin-top: 0.5rem;
  padding: 0.35rem 0.85rem;
  background: ${theme.colors.primary};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  &:hover { opacity: 0.9; }
`;

export const DragDropPriorityRanking = ({ 
  availableCompanies = [], 
  selectedCompanies = [],
  onUpdate,
  maxSelections = 15,
  userLastLogin
}) => {
  const [available, setAvailable] = useState(availableCompanies);
  const [selected, setSelected] = useState(selectedCompanies);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const toggleExpand = (e, companyId) => {
    e.stopPropagation();
    setExpandedId(prev => prev === companyId ? null : companyId);
  };

  const getPlaceLabel = (value) => {
    const opt = PLACE_DU_DESIGN_OPTIONS.find(o => o.value === value);
    return opt ? opt.label_fr : value;
  };

  const getProfilLabel = (value) => {
    const opt = PROFIL_CREATEUR_OPTIONS.find(o => o.value === value);
    return opt ? opt.label_fr : value;
  };

  useEffect(() => {
    setAvailable(availableCompanies.filter(company =>
      !selected.find(s => s.id === company.id)
    ));
  }, [availableCompanies, selected]);

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(selected);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const updatedItems = items.map((item, index) => ({
      ...item,
      priority: index + 1
    }));

    setSelected(updatedItems);
    if (onUpdate) onUpdate(updatedItems);
  };

  const addCompany = (company) => {
    if (selected.length >= maxSelections) {
      alert(`Vous ne pouvez sélectionner que ${maxSelections} entreprises maximum.`);
      return;
    }

    const newSelection = {
      ...company,
      priority: selected.length + 1
    };

    const updatedSelected = [...selected, newSelection];
    setSelected(updatedSelected);
    
    if (onUpdate) onUpdate(updatedSelected);
  };

  const removeCompany = (companyId) => {
    const updatedSelected = selected
      .filter(c => c.id !== companyId)
      .map((item, index) => ({
        ...item,
        priority: index + 1
      }));

    setSelected(updatedSelected);
    if (onUpdate) onUpdate(updatedSelected);
  };

  const isNewCompany = (company) => {
    if (!userLastLogin) return false;
    return new Date(company.createdAt) > new Date(userLastLogin);
  };

  const filteredAvailable = available.filter(company => {
    const term = searchTerm.toLowerCase();
    const meta = company._raw?.metadata || {};
    return company.name.toLowerCase().includes(term) ||
           meta.offre?.toLowerCase().includes(term) ||
           meta.savoir_faire?.toLowerCase().includes(term);
  });

  return (
    <Container>
      <Section>
        <SectionHeader>
          <SectionTitle>
            <FaBuilding />
            Entreprises disponibles
            <Badge variant="secondary">{filteredAvailable.length}</Badge>
          </SectionTitle>
        </SectionHeader>
        
        <div style={{ padding: theme.spacing.md }}>
          <SearchInput
            type="text"
            placeholder="Rechercher une entreprise..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <CompanyList>
          {filteredAvailable.map(company => {
            const isExpanded = expandedId === company.id;
            const meta = company._raw?.metadata || {};
            const projects = company._raw?.projects || [];
            return (
              <CompanyCard
                key={company.id}
                onClick={() => !isExpanded && addCompany(company)}
                selected={false}
                style={isExpanded ? { cursor: 'default' } : {}}
              >
                <CompanyName>
                  {company.name}
                  {isNewCompany(company) && <NewTag>Nouveau</NewTag>}
                  {company.validatedAt && (
                    <span style={{ fontSize: '0.75rem', color: theme.colors.gray[400], fontWeight: 400, marginLeft: 'auto' }}>
                      {new Date(company.validatedAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                    </span>
                  )}
                </CompanyName>
                <CompanyDetails>
                  {company.location && (
                    <DetailItem>
                      <FaMapMarker size={12} />
                      <a href={company.location.startsWith('http') ? company.location : `https://${company.location}`} target="_blank" rel="noreferrer" style={{ color: theme.colors.primary, textDecoration: 'underline' }} onClick={e => e.stopPropagation()}>
                        {company.location.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}
                      </a>
                    </DetailItem>
                  )}
                </CompanyDetails>
                {!isExpanded && company.description && (
                  <CompanyDescription>
                    {makeUrlsAndEmailsLinks(
                      company.description.substring(0, 150) + (company.description.length > 150 ? '...' : '')
                    )}
                  </CompanyDescription>
                )}
                <ExpandToggle onClick={(e) => toggleExpand(e, company.id)}>
                  {isExpanded ? <><FaEyeSlash size={11} /> Masquer l'annonce</> : <><FaEye size={11} /> Voir l'annonce complète</>}
                </ExpandToggle>
                {isExpanded && (
                  <ExpandedPanel onClick={e => e.stopPropagation()}>
                    {meta.offre && (
                      <DetailBlock>
                        <div className="detail-label">Offre / Typologie</div>
                        <div className="detail-value">{makeUrlsAndEmailsLinks(meta.offre)}</div>
                      </DetailBlock>
                    )}
                    {meta.savoir_faire && (
                      <DetailBlock>
                        <div className="detail-label">Savoir-faire, matériaux et techniques</div>
                        <div className="detail-value">{makeUrlsAndEmailsLinks(meta.savoir_faire)}</div>
                      </DetailBlock>
                    )}
                    {meta.place_du_design?.length > 0 && (
                      <DetailBlock>
                        <div className="detail-label">Place du design</div>
                        <AnnonceTagList>
                          {meta.place_du_design.map((v, i) => (
                            <AnnonceTag key={i}>{getPlaceLabel(v)}</AnnonceTag>
                          ))}
                        </AnnonceTagList>
                      </DetailBlock>
                    )}
                    {meta.comments && meta.comments.replace(/<[^>]*>/g, '').trim() && (
                      <DetailBlock>
                        <div className="detail-label">Commentaires, précisions</div>
                        <div className="detail-value" style={{ whiteSpace: 'normal' }} dangerouslySetInnerHTML={{ __html: meta.comments }} />
                      </DetailBlock>
                    )}
                    {projects.length > 0 && (
                      <DetailBlock>
                        <div className="detail-label">Projets ({projects.length})</div>
                        {projects.map((p, i) => (
                          <ProjectItem key={i}>
                            <div className="proj-title">Projet {i + 1}{p.typologie ? ` — ${p.typologie}` : ''}</div>
                            {p.description && <div className="proj-detail">{makeUrlsAndEmailsLinks(p.description)}</div>}
                            {p.savoir_faire && <div className="proj-detail">Savoir-faire recherché : {makeUrlsAndEmailsLinks(p.savoir_faire)}</div>}
                            {p.profil_createur?.length > 0 && (
                              <AnnonceTagList style={{ marginTop: 4 }}>
                                {p.profil_createur.map((v, j) => (
                                  <AnnonceTag key={j}>{getProfilLabel(v)}</AnnonceTag>
                                ))}
                              </AnnonceTagList>
                            )}
                          </ProjectItem>
                        ))}
                      </DetailBlock>
                    )}
                    <AddBtn onClick={() => addCompany(company)}>
                      + Ajouter à ma sélection
                    </AddBtn>
                  </ExpandedPanel>
                )}
              </CompanyCard>
            );
          })}
          
          {filteredAvailable.length === 0 && (
            <EmptyState>
              <FaBuilding />
              <p>Aucune entreprise trouvée</p>
            </EmptyState>
          )}
        </CompanyList>
      </Section>

      <Section>
        <SectionHeader>
          <SectionTitle>
            <FaTrophy />
            Vos sélections
            <Badge variant="primary">{selected.length}/{maxSelections}</Badge>
          </SectionTitle>
        </SectionHeader>

        {selected.length >= maxSelections && (
          <WarningMessage>
            <FaExclamationTriangle />
            Vous avez atteint le nombre maximum de sélections
          </WarningMessage>
        )}

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="selected">
            {(provided, snapshot) => (
              <DroppableArea
                {...provided.droppableProps}
                ref={provided.innerRef}
                isDraggingOver={snapshot.isDraggingOver}
              >
                {selected.map((company, index) => (
                  <Draggable 
                    key={company.id} 
                    draggableId={company.id} 
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <DraggableCard
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        isDragging={snapshot.isDragging}
                      >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <DragHandle {...provided.dragHandleProps}>
                            <FaGripVertical />
                          </DragHandle>
                          <PriorityNumber priority={company.priority}>
                            {company.priority}
                          </PriorityNumber>
                          <CardContent>
                            <div style={{ flex: 1 }}>
                              <CompanyName style={{ marginBottom: '4px' }}>
                                {company.name}
                              </CompanyName>
                              <div style={{ fontSize: '0.875rem', color: theme.colors.gray[500] }}>
                                {company.location}
                              </div>
                            </div>
                            <button
                              onClick={() => removeCompany(company.id)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: theme.colors.error,
                                cursor: 'pointer',
                                padding: theme.spacing.xs,
                              }}
                            >
                              ✕
                            </button>
                          </CardContent>
                        </div>
                      </DraggableCard>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
                
                {selected.length === 0 && (
                  <EmptyState>
                    <FaPlus />
                    <p>Cliquez sur une entreprise pour l'ajouter</p>
                    <p style={{ marginTop: theme.spacing.xs, fontSize: '0.75rem' }}>
                      Puis glissez-déposez pour ordonner par priorité
                    </p>
                  </EmptyState>
                )}
              </DroppableArea>
            )}
          </Droppable>
        </DragDropContext>
      </Section>
    </Container>
  );
};